import { Controller } from "@hotwired/stimulus"

// Default “presence” error messages per validation scope
const PRESENCE_MESSAGES = {
  bol:             "Bill of Lading cannot be blank.",
  bookings:        "Booking cannot be blank.",
  containers:      "Container cannot be blank.",
  deliveryOrders:  "Delivery order cannot be blank.",
  exportReleases:  "Export release cannot be blank.",
  shipmentNumbers: "Shipment number cannot be blank.",
}

/**
 * Validates each panel of the Truck Gate‑In form.
 * On input change it calls the relevant JSON endpoint
 * and surfaces any server‑side errors inline.
 */
export default class extends Controller {
  static outlets = ['truck-gate-in--form-manager']
  static targets = [
    "emptyBooking", "emptyBookingErrors",
    "emptyContainer", "emptyContainerErrors",
    "fullDeliveryOrder", "fullDeliveryOrderErrors",
    "emptyExportRelease", "emptyExportReleaseErrors",
    "emptyGenset", "emptyGensetErrors",
    "emptyGensetFields",
    "emptyShipmentNumber", "emptyShipmentNumberErrors",
    "fullBol", "fullBolErrors",
    "fullBooking", "fullBookingErrors",
    "fullContainer", "fullContainerErrors",
    "fullExportRelease", "fullExportReleaseErrors",
    "fullGenset", "fullGensetErrors",
    "fullGensetFields",
    "fullShipmentNumber", "fullShipmentNumberErrors",
    "usage",
  ]

  static values = {
    purposeType: String, // "drop_off"  or "pick_up" – supplied by each panel template
    consigneeWhitelist: Array,
  }

  // single source of mutation for every error message
  errors = this.#initialErrors()

  connect() {
    // Unique lock key for this validations panel
    this.presenceLockKey = this.presenceLockKey || `presence:${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;

    const csrfToken = document
      .querySelector('meta[name="csrf-token"]')
      .content

    this.requestHeaders = {
      Accept: "application/json",
      "X-CSRF-Token": csrfToken,
    }

    // Holds AbortControllers for in‑flight validation requests.
    // Key = logical validator name (e.g. "bookingConflicts:full").
    // Before starting a new fetch we abort any previous one with
    // the same key so the UI never shows stale errors.
    this.pending = new Map()

    // Record every lock this panel adds to the form manager
    this.activeLocks = new Set()

    // Cache for /containers/:id.json responses
    this.containerCache = new Map()
  }

  disconnect () {
    // When the row is deleted, release all locks
    if (this.hasTruckGateInFormManagerOutlet) {
      this.activeLocks.forEach(key =>
        this.truckGateInFormManagerOutlet.unlockSubmission(key)
      )
    }
  }

  /* ------------------------------------------------------------------
   * PUBLIC API (called from the DOM)
   * ---------------------------------------------------------------- */

  async pickUpFullContainerChecks() {
    const { id: containerId, status } = this.#currentContainer()
    await this.#checkContainerGateMismatch({ containerId, status })
    await this.#checkContainerHolds({ containerId, status })
    await this.#checkConsigneeWhitelist({ containerId, status })
  }

  async pickUpEmptyContainerChecks() {
    const { id: containerId, status } = this.#currentContainer()
    await this.#checkContainerHolds({ containerId, status })
    await this.#checkConsigneeWhitelist({ containerId, status })

    const { id: bookingId } = this.#currentBooking()
    if (!containerId || !bookingId) {
      return this.#clearErrors('containers', status, "bookingConflicts")
    }
    await this.#validateContainerBookingConflicts({ containerId, bookingId, status })
  }

  async dropOffFullContainerChecks() {
    const { id: containerId, status } = this.#currentContainer()
    await this.#checkContainerHolds({ containerId, status })

    const { id: bookingId } = this.#currentBooking()
    if (!containerId || !bookingId) {
      return this.#clearErrors('containers', status, "bookingConflicts")
    }

    if (this.hasUsageTarget) {
      const checked = this.usageTargets.find(el => el.checked)
      if (checked && checked.value !== "export") {
        return this.#clearErrors('containers', status, "bookingConflicts")
      }
    }

    await this.#validateContainerBookingConflicts({ containerId, bookingId, status })
  }

  async dropOffEmptyContainerChecks() {
    const { id: containerId, status } = this.#currentContainer()
    await this.#checkContainerHolds({ containerId, status })
  }

  /*
  *  Required field presence checks.
  *  Gets called from the form manager
  *  when the user submits the form.
  */
  runPresenceChecks() {
    const checks = [
      // BOOKINGS
      {
        target: () => this.hasFullBookingTarget  && this.fullBookingTarget,
        scope:  'bookings',
        status: 'full',
        update: s => this.#updateCombinedBookingErrors(s),
      },
      {
        target: () => this.hasEmptyBookingTarget && this.emptyBookingTarget,
        scope:  'bookings',
        status: 'empty',
        update: s => this.#updateCombinedBookingErrors(s),
      },

      // BILL OF LADING (only “full” panel has this field)
      {
        target: () => this.hasFullBolTarget && this.fullBolTarget,
        scope:  'bol',
        status: 'full',
        update: s => this.#updateCombinedBolErrors(s),
      },

      // CONTAINERS
      {
        target: () => this.hasFullContainerTarget && this.fullContainerTarget,
        scope:  'containers',
        status: 'full',
        update: s => this.#updateCombinedContainerErrors(s),
      },
      {
        target: () => this.hasEmptyContainerTarget && this.emptyContainerTarget,
        scope:  'containers',
        status: 'empty',
        update: s => this.#updateCombinedContainerErrors(s),
      },

      // SHIPMENT NUMBER (STT/STC depots, full only)
      {
        target: () => this.hasFullShipmentNumberTarget && this.fullShipmentNumberTarget,
        scope:  'shipmentNumbers',
        status: 'full',
        update: s => this.#updateCombinedShipmentNumberErrors(s),
      },

      // EXPORT RELEASES
      {
        target: () => this.hasFullExportReleaseTarget && this.fullExportReleaseTarget,
        scope:  'exportReleases',
        status: 'full',
        update: s => this.#updateCombinedExportReleaseErrors(s),
      },
      {
        target: () => this.hasEmptyExportReleaseTarget && this.emptyExportReleaseTarget,
        scope:  'exportReleases',
        status: 'empty',
        update: s => this.#updateCombinedExportReleaseErrors(s),
      },

      // DELIVERY ORDERS (full only)
      {
        target: () => this.hasFullDeliveryOrderTarget && this.fullDeliveryOrderTarget,
        scope:  'deliveryOrders',
        status: 'full',
        update: s => this.#updateCombinedDeliveryOrderErrors(s),
      },
    ]

    checks.forEach(cfg => {
      const el = cfg.target?.()
      if (!el) return // field isn’t present in this panel

      if (this.#shouldValidate(el)) {
        const hasValue = !!el.value
        const msg = PRESENCE_MESSAGES[cfg.scope] || "This field cannot be blank."
        this.errors[cfg.scope][cfg.status].presence = hasValue ? [] : [msg]
      } else {
        this.#clearErrors(cfg.scope, cfg.status, 'presence')
      }
      cfg.update(cfg.status)
    })

    this.#syncPresenceLock()
  }

  /**
   * Clears the specific presence‑error bucket when a required Select2
   * field receives a value. Attach with:
   *   data-action="select2:change->truck-gate-in--validations#clearPresenceOnChange"
   */
  clearPresenceOnChange(event) {
    const el = event.target
    if (!el || !el.value?.trim()) return // nothing selected

    // Map element → scope / status
    const map = [
      { ref: () => this.hasFullBookingTarget  && this.fullBookingTarget,   scope:'bookings',   status:'full'  },
      { ref: () => this.hasEmptyBookingTarget && this.emptyBookingTarget,  scope:'bookings',   status:'empty' },
      { ref: () => this.hasFullBolTarget      && this.fullBolTarget,       scope:'bol',        status:'full'  },
      { ref: () => this.hasFullContainerTarget&& this.fullContainerTarget, scope:'containers', status:'full'  },
      { ref: () => this.hasEmptyContainerTarget&&this.emptyContainerTarget,scope:'containers', status:'empty' },
      { ref: () => this.hasFullExportReleaseTarget && this.fullExportReleaseTarget, scope:'exportReleases', status:'full' },
      { ref: () => this.hasEmptyExportReleaseTarget && this.emptyExportReleaseTarget, scope:'exportReleases', status:'empty' },
      { ref: () => this.hasFullDeliveryOrderTarget && this.fullDeliveryOrderTarget, scope:'deliveryOrders', status:'full' },
      { ref: () => this.hasFullShipmentNumberTarget && this.fullShipmentNumberTarget, scope:'shipmentNumbers', status:'full' },
      { ref: () => this.hasEmptyShipmentNumberTarget && this.emptyShipmentNumberTarget, scope:'shipmentNumbers', status:'empty' },
    ]

    const entry = map.find(m => m.ref() === el)
    if (!entry) return // not a tracked required field

    // Wipe presence errors for this field's scope / status
    this.#clearErrors(entry.scope, entry.status, 'presence')
    this.#syncPresenceLock()
  }

  /**
   * Called by purpose‑rows controller whenever the user flips
   * container status (full / empty). It wipes *presence* errors
   * for the side that is now hidden and re‑syncs the submit
   * lock without running a full validation pass.
   */
  clearPresenceChecks(status) {
    if (!status) return

    // Presence buckets that need to be cleared for the hidden side
    const scopes = [
      'bol',
      'bookings',
      'containers',
      'exportReleases',
      'gensets',
      'shipmentNumbers',
    ]

    scopes.forEach(scope =>
      this.#clearErrors(scope, status, 'presence')
    )

    // If no presence errors remain on either side, release the lock
    const stillHasPresence = this.#hasPresenceErrors()
    const formMgr = this.hasTruckGateInFormManagerOutlet && this.truckGateInFormManagerOutlet

    if (!formMgr) return

    if (!stillHasPresence && this.activeLocks.has(this.presenceLockKey)) {
      formMgr.unlockSubmission(this.presenceLockKey)
      this.activeLocks.delete(this.presenceLockKey)
    } else if (stillHasPresence && !this.activeLocks.has(this.presenceLockKey)) {
      // (edge case: manual invocation before any runPresenceChecks call)
      formMgr.lockSubmission(this.presenceLockKey)
      this.activeLocks.add(this.presenceLockKey)
    }
  }

  async checkContainerAtTerminal() {
    const { id: containerId, status } = this.#currentContainer()
    if (!containerId) {
      ["full", "empty"].forEach(s => {
        this.#clearErrors('containers', s, "atTerminal")
        this.#toggleGensetFields(null, s)
      })
      return
    }

    await this.#run("containerAtTerminal", async () => {
      const data = await this.#getContainerData(containerId)
      const container = data.container

      this.errors.containers[status].atTerminal =
        (container?.current_location_type || '').toLowerCase() === 'terminal'
          ? [`WARNING: This container is at ${container.current_location}`]
          : []

      this.#updateCombinedContainerErrors(status)
      this.#toggleGensetFields(container, status)
    })
  }

  async checkGensetAtTerminal() {
    const { id: gensetId, status } = this.#currentGenset()
    if (!gensetId) {
      this.#clearErrors('gensets', status, 'atTerminal')
      return
    }

    await this.#run("gensetAtTerminal", async () => {
      const data = await this.#getJSON(`/gensets/${gensetId}.json`)
      this.errors.gensets[status].atTerminal =
        (data.current_location_type || '').toLowerCase() === 'terminal'
          ? [`WARNING: This genset is at ${data.current_location_name}.`]
          : []

      this.#updateCombinedGensetErrors(status)
    })
  }

  async checkBookingExpiry() {
    const { id: bookingId, status } = this.#currentBooking()
    if (!bookingId) {
      ["full", "empty"].forEach(s => {
        this.#clearErrors('bookings', s, "expired")
        this.#clearErrors('bookings', s, "voyagePastCutOff")
      })
      return
    }

    await this.#run("bookingExpiry", async () => {
      const data = await this.#getJSON(`/bookings/${bookingId}/voyage.json`)
      this.#evaluateBookingExpiry(data, status)
      this.#evaluateVoyageCutOff(data, status)

      this.#updateCombinedBookingErrors(status)
    })
  }

  #evaluateBookingExpiry(data, status) {
    this.errors.bookings[status].expired = data.booking_expired ? ["The selected booking has expired."] : []
  }

  #evaluateVoyageCutOff(data, status) {
    const lockKey = `${status}-voyagePastCutOff`
    const formMgr = this.hasTruckGateInFormManagerOutlet && this.truckGateInFormManagerOutlet

    // Only drop‑off panels should ever block form submission
    const isDropOff = this.purposeTypeValue === 'drop_off'

    const cutOffTs = data.receiving_cut_off_time ? Date.parse(data.receiving_cut_off_time) : null
    const passed = cutOffTs && Date.now() > cutOffTs

    let messages = []
    let shouldLock = false

    if (passed) {
      const base = "The voyage cut-off time has passed."
      if (data.block_receiving_export_past_cut_off) {
        messages = [`${base} This voyage won't accept more cargo.`]
        shouldLock = true
      } else {
        messages = [`${base} You may still receive more cargo for the voyage.`]
      }
    }

    this.errors.bookings[status].voyagePastCutOff = messages

    if (!formMgr) return

    if (isDropOff && shouldLock) {
      // Add the lock only once
      if (!this.activeLocks.has(lockKey)) {
        formMgr.lockSubmission(lockKey)
        this.activeLocks.add(lockKey)
      }
    } else {
      // Make sure we release it if present
      if (this.activeLocks.has(lockKey)) {
        formMgr.unlockSubmission(lockKey)
        this.activeLocks.delete(lockKey)
      }
    }
  }

  /* booking × trucking‑company (match) */
  async checkBookingAndTruckingCompany() {
    return this.#bookingTruckingValidator({
      endpoint: "/truck_visits/pre_gate_in/checks/booking_trucking_company",
      key: "truckingCompanies",
    })
  }

  /* booking × trucking‑company (allowed shipping lines) */
  async checkBookingAndTruckingCompanyAllowedShippingLines() {
    return this.#bookingTruckingValidator({
      endpoint:
        "/truck_visits/pre_gate_in/checks/booking_trucking_company_allowed_shipping_lines",
      key: "allowedShippingLines",
    })
  }

  /* B/L × trucking‑company (allowed shipping lines) */
  async checkBolAndTruckingCompanyAllowedShippingLines() {
    const { id: bolId, status } = this.#currentBol()
    if (!bolId) return this.#clearErrors("bol", status, "allowedShippingLines")

    await this.#run(`bolAllowedLines:${status}`, async () => {
      const url = this.#buildUrl(
        "/truck_visits/pre_gate_in/checks/bol_segment_trucking_company_allowed_shipping_lines",
        {
          bol_segment_id: bolId,
          truck_id: this.#truckId,
          truck_driver_id: this.#truckDriverId,
        },
      )

      const { ok, message } = await this.#getJSON(url, { raw: true })
      this.errors.bol[status].allowedShippingLines = ok ? [] : [message]
      this.#updateCombinedBolErrors(status)
    })
  }

  /* Called from trucks_controller.js via outlets */
  setConsigneeWhitelist(list = [], companyName = null) {
    // Store as an array of integers so type mismatches (string vs number) can't occur
    this.consigneeWhitelistValue = list
      .filter(id => id != null)
      .map(id => Number(id))

    this.truckingCompanyName = companyName

    // re‑check immediately if a container is already chosen
    const { id, status } = this.#currentContainer()
    if (id) this.#checkConsigneeWhitelist({ containerId: id, status })
  }

  /* ------------------------------------------------------------------
   * PRIVATE HELPERS
   * ---------------------------------------------------------------- */

  #initialErrors() {
    return {
      bol: {
        full: {
          allowedShippingLines: [],
          presence: [],
        }
      },
      bookings: {
        full:  {
          allowedShippingLines: [],
          expired: [],
          presence: [],
          truckingCompanies: [],
          voyagePastCutOff: [],
        },
        empty: {
          allowedShippingLines: [],
          expired: [],
          presence: [],
          truckingCompanies: [],
          voyagePastCutOff: [],
        },
      },
      containers: {
        full:  {
          atTerminal: [],
          bookingConflicts: [],
          consigneeWhitelist: [],
          holds: [],
          presence: [],
          terminalMismatch: [],
        },
        empty: {
          atTerminal: [],
          bookingConflicts: [],
          consigneeWhitelist: [],
          holds: [],
          presence: [],
          terminalMismatch: [],
        },
      },
      exportReleases: {
        full: { presence: [] },
      },
      gensets: {
        full:  { atTerminal: [] },
        empty: { atTerminal: [] },
      },
      shipmentNumbers: {
        full: { presence: [] },
      },
    }
  }

  /* ---------- core validators ---------- */

  // Validates that the selected container is compatible with the chosen booking.
  async #validateContainerBookingConflicts({ containerId, bookingId, status }) {
    await this.#run(`bookingConflicts:${status}`, async () => {
      const url = this.#buildUrl("/bookings/container_checks/validate", {
        container_id: containerId,
        booking_id: bookingId,
      })

      const { ok, message } = await this.#getJSON(url, { raw: true })
      this.errors.containers[status].bookingConflicts =
        ok ? [] : message ? [message] : []

      this.#updateCombinedContainerErrors(status)
    })
  }

  async #bookingTruckingValidator({ endpoint, key }) {
    const { id: bookingId, status } = this.#currentBooking()
    if (!bookingId) {
      this.#clearErrors('bookings', status, key)
      return
    }

    await this.#run(`${endpoint}:${status}`, async () => {
      const url = this.#buildUrl(endpoint, {
        booking_id: bookingId,
        truck_id: this.#truckId,
        truck_driver_id: this.#truckDriverId,
      })

      const { ok, message } = await this.#getJSON(url, { raw: true })
      this.errors.bookings[status][key] = ok ? [] : [message]
      this.#updateCombinedBookingErrors(status)
    })
  }

  async #checkContainerHolds({ containerId, status }) {
    if (!containerId) {
      this.#clearErrors('containers', status, "holds")
      return
    }

    await this.#run(`holds:${containerId}`, async () => {
      const url = `/containers/${containerId}/holds.json?hold_type=unlifted`
      const holds = await this.#getJSON(url)
      this.errors.containers[status].holds =
        holds.length
          ? [`This container has holds that have not been lifted: ${holds.join(", ")}`]
          : []

      this.#updateCombinedContainerErrors(status)
    })
  }

  async #checkConsigneeWhitelist({ containerId, status }) {
    // Nothing to do if there is no whitelist
    if (!containerId || !this.consigneeWhitelistValue.length) {
      return this.#clearErrors("containers", status, "consigneeWhitelist")
    }

    // Get the container’s consignee_id
    const data = await this.#getContainerData(containerId)
    const consigneeId = data.container?.consignee_id

    const allowed = this.consigneeWhitelistValue.includes(Number(consigneeId))
    const company = this.truckingCompanyName || "This trucking company"
    this.errors.containers[status].consigneeWhitelist = allowed
      ? []
      : [`${company} is not allowed to pick up containers for this consignee.`]

    this.#updateCombinedContainerErrors(status)
  }

  async #checkContainerGateMismatch({ containerId, status }) {
    const formMgr = this.hasTruckGateInFormManagerOutlet && this.truckGateInFormManagerOutlet
    const gateTerminal = formMgr?.currentTerminalName?.() || null

    if (!containerId || !gateTerminal) {
      this.#clearErrors('containers', status, 'terminalMismatch')
      return
    }

    const gateName = formMgr?.currentGateName?.() || "The selected gate"
    const data = await this.#getContainerData(containerId)
    const container = data.container
    const mismatch = container &&
      (container.current_location_type || '').toLowerCase() === 'terminal' &&
      container.current_location_name &&
      container.current_location_name !== gateTerminal

    this.errors.containers[status].terminalMismatch = mismatch
      ? [
          `${gateName} is not at the same terminal as this container. ` +
          `Container ${container.number} was last reported to be at ${container.current_location_name}.`,
        ] : []

    this.#updateCombinedContainerErrors(status)
  }

  /* ---------- Utilities ---------- */

  #buildUrl(path, params = {}) {
    const u = new URL(path, window.location.origin)
    u.search = new URLSearchParams(
      Object.entries(params).filter(([, v]) => v),
    )
    return u.toString()
  }

  /**
   * Wraps an async validation task with automatic cancellation.
   * If another task with the same key is triggered, the previous
   * fetch is aborted to prevent race conditions and stale messages.
   */
  async #run(key, fn) {
    if (this.pending.has(key)) this.pending.get(key).abort()
    const controller = new AbortController()
    this.pending.set(key, controller)
    try {
      return await fn({ signal: controller.signal })
    } finally {
      this.pending.delete(key)
    }
  }

  async #getJSON(url, { raw = false } = {}) {
    const response = await fetch(url, { headers: this.requestHeaders })
    if (raw) {
      let data = {}
      try {
        data = await response.json()
      } catch {}
      return { ok: response.ok, ...data }
    }
    return response.json()
  }

  /**
   * Fetches /containers/:id.json at most once per container ID.
   * Subsequent calls return the cached copy.
   */
  async #getContainerData(containerId) {
    if (!containerId) return null
    if (this.containerCache.has(containerId)) {
      return this.containerCache.get(containerId)
    }
    const data = await this.#getJSON(`/containers/${containerId}.json`)
    this.containerCache.set(containerId, data)
    return data
  }

  get #truckId() {
    return document.getElementById("truck_visit_truck_id")?.value
  }

  get #truckDriverId() {
    return document.getElementById("truck_visit_truck_driver_id")?.value
  }

  #currentContainer() {
    const full  = this.hasFullContainerTarget  ? this.fullContainerTarget.value  : ""
    const empty = this.hasEmptyContainerTarget ? this.emptyContainerTarget.value : ""
    if (full && !empty) return { id: full,  status: "full" }
    if (empty)          return { id: empty, status: "empty" }
    return { id: null }
  }

  #currentBooking() {
    const full  = this.hasFullBookingTarget  ? this.fullBookingTarget.value  : ""
    const empty = this.hasEmptyBookingTarget ? this.emptyBookingTarget.value : ""
    if (full && !empty) return { id: full,  status: "full" }
    if (empty)          return { id: empty, status: "empty" }
    return { id: null }
  }

  #currentBol() {
    if (!this.hasFullBolTarget) return { id: null }
    const bol = this.fullBolTarget.value
    return bol ? { id: bol, status: "full" } : { id: null }
  }

  #currentGenset() {
    const full  = this.hasFullGensetTarget  ? this.fullGensetTarget.value  : ""
    const empty = this.hasEmptyGensetTarget ? this.emptyGensetTarget.value : ""
    if (full && !empty) return { id: full,  status: "full" }
    if (empty)          return { id: empty, status: "empty" }
    return { id: null }
  }

  #updateCombinedGensetErrors(status) {
    const e = this.errors.gensets[status]
    this.#renderGensetErrors(status, [...e.atTerminal])
  }

  #renderGensetErrors(status, messages) {
    if (status === "full" && this.hasFullGensetErrorsTarget) {
      this.#render(this.fullGensetErrorsTarget, messages)
    } else if (status === "empty" && this.hasEmptyGensetErrorsTarget) {
      this.#render(this.emptyGensetErrorsTarget, messages)
    }
  }

  #toggleGensetFields(container, status) {
    const isReefer = !!(container && container.is_reefer)

    // Safely resolve the wrapper target
    const fieldsTarget =
      status === 'full'
        ? (this.hasFullGensetFieldsTarget ? this.fullGensetFieldsTarget : null)
        : (this.hasEmptyGensetFieldsTarget ? this.emptyGensetFieldsTarget : null)

    const selectEl =
      status === 'full'
        ? (this.hasFullGensetTarget ? this.fullGensetTarget : null)
        : (this.hasEmptyGensetTarget ? this.emptyGensetTarget : null)

    if (!fieldsTarget) return

    if (isReefer) {
      fieldsTarget.style.display = ''
    } else {
      // clear any previously selected genset then hide the fields
      if (selectEl) {
        selectEl.value = ''
        selectEl.dispatchEvent(new Event('change', { bubbles: true }))
      }

      // also clear any genset errors since the field is no longer shown
      this.errors.gensets[status].atTerminal = []
      this.#updateCombinedGensetErrors(status)

      fieldsTarget.style.display = 'none'
    }
  }


  /* ---------- error bookkeeping + rendering ---------- */

  /**
   * Clears one error bucket. If `status` is undefined we clear the key
   * for both "full" and "empty" panels so callers don’t have to specify.
   *
   * @param {"containers"|"bookings"|"bol"} scope
   * @param {"full"|"empty"|undefined}      status
   * @param {string}                        key
   */
  #clearErrors(scope, status, key) {
    const statuses = status ? [status] : ["full", "empty"]

    statuses.forEach(s => {
      // Guard against missing structure to avoid TypeError
      if (this.errors?.[scope]?.[s]) {
        this.errors[scope][s][key] = []
      }
    })

    // Re‑render combined lists
    statuses.forEach(s => this.#updateCombinedErrors(scope, s))
  }

  /**
   * Refresh the combined error block for the given scope / status.
   * Centralises the switch so we only have it in one place.
   */
  #updateCombinedErrors(scope, status) {
    switch (scope) {
      case 'bol':
        this.#updateCombinedBolErrors(status);            break;
      case 'bookings':
        this.#updateCombinedBookingErrors(status);        break;
      case 'containers':
        this.#updateCombinedContainerErrors(status);      break;
      case 'deliveryOrders':
        this.#updateCombinedDeliveryOrderErrors(status);  break;
      case 'exportReleases':
        this.#updateCombinedExportReleaseErrors(status);  break;
      case 'gensets':
        this.#updateCombinedGensetErrors(status);         break;
      case 'shipmentNumbers':
        this.#updateCombinedShipmentNumberErrors(status); break;
    }
  }

  #updateCombinedContainerErrors(status) {
    const e = this.errors.containers[status]
    this.#renderContainerErrors(
      status,
      [
        ...e.atTerminal,
        ...e.bookingConflicts,
        ...e.consigneeWhitelist,
        ...e.holds,
        ...e.presence,
        ...e.terminalMismatch,
      ],
    )
  }

  #updateCombinedBookingErrors(status) {
    const e = this.errors.bookings[status]
    this.#renderBookingErrors(
      status,
      [
        ...e.allowedShippingLines,
        ...e.expired,
        ...e.presence,
        ...e.truckingCompanies,
        ...e.voyagePastCutOff,
      ],
    )
  }

  #updateCombinedShipmentNumberErrors(status) {
    const e = this.errors.shipmentNumbers[status]
    if (status === "full" && this.hasFullShipmentNumberErrorsTarget) {
      this.#render(this.fullShipmentNumberErrorsTarget, [...e.presence])
    }
  }

  #updateCombinedExportReleaseErrors(status) {
    const e = this.errors.exportReleases[status]
    if (status === "full" && this.hasFullExportReleaseErrorsTarget) {
      this.#render(this.fullExportReleaseErrorsTarget, [...e.presence])
    }
  }

  #updateCombinedDeliveryOrderErrors(status) {
    const e = this.errors.deliveryOrders[status]
    if (status === "full" && this.hasFullDeliveryOrderErrorsTarget) {
      this.#render(this.fullDeliveryOrderErrorsTarget, [...e.presence])
    }
  }

  #updateCombinedBolErrors(status) {
    const e = this.errors.bol[status]
    if (status === "full" && this.hasFullBolErrorsTarget) {
      this.#render(this.fullBolErrorsTarget, [
        ...e.allowedShippingLines,
        ...e.presence,
      ])
    }
  }

  #renderContainerErrors(status, messages) {
    if (status === "full" && this.hasFullContainerErrorsTarget) {
      this.#render(this.fullContainerErrorsTarget, messages)
    } else if (status === "empty" && this.hasEmptyContainerErrorsTarget) {
      this.#render(this.emptyContainerErrorsTarget, messages)
    }
  }

  #renderBookingErrors(status, messages) {
    if (status === "full" && this.hasFullBookingErrorsTarget) {
      this.#render(this.fullBookingErrorsTarget, messages)
    } else if (status === "empty" && this.hasEmptyBookingErrorsTarget) {
      this.#render(this.emptyBookingErrorsTarget, messages)
    }
  }

  #render(target, messages) {
    if (!target) return
    const html = messages.length
      ? `<div class="alert alert-danger" role="alert">
          <ul style="padding: 0em 0em 0em 1em;">
            ${messages.map(m => `<li><p style="margin-bottom: 0;">${m}</p></li>`).join("")}
          </ul>
        </div>`
      : ""

    if (target.dataset.prevHtml !== html) {
      target.dataset.prevHtml = html
      target.innerHTML = html
    }
  }

  /*
  * Adds or removes this panel's presence lock on the Form‑Manager
  * depending on whether any presence errors remain.
  */
  #syncPresenceLock() {
    if (!this.hasTruckGateInFormManagerOutlet) return

    const formMgr   = this.truckGateInFormManagerOutlet
    const hasErrors = this.#hasPresenceErrors()

    if (hasErrors && !this.activeLocks.has(this.presenceLockKey)) {
      formMgr.lockSubmission(this.presenceLockKey)
      this.activeLocks.add(this.presenceLockKey)
    } else if (!hasErrors && this.activeLocks.has(this.presenceLockKey)) {
      formMgr.unlockSubmission(this.presenceLockKey)
      this.activeLocks.delete(this.presenceLockKey)
    }
  }

  /**
   * Returns `true` if *any* presence‑error bucket in this panel is non‑empty.
   * Used by runPresenceChecks() to decide whether to lock/unlock submission.
   */
  #hasPresenceErrors() {
    const scopes = [
      "bol",
      "bookings",
      "containers",
      "deliveryOrders",
      "exportReleases",
      "shipmentNumbers",
    ];

    return scopes.some((scope) =>
      ["full", "empty"].some(
        (status) => (this.errors[scope]?.[status]?.presence || []).length > 0,
      ),
    );
  }

  #shouldValidate(el) {
    return !!(
      el &&
      el.offsetParent !== null &&
      el.dataset.requiredValue === 'true'
    )
  }
}
